import { useState, useEffect, Suspense } from 'react';
import '@/App.css';
import '@/i18n';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster, toast } from 'sonner';
import { recordVisit, togglePin, dismissPinSuggestion, ROUTE_LABELS } from '@/utils/recentlyVisited';
import { ImageProtectionProvider } from '@/components/ImageProtection';
import { initGA, trackPageView } from '@/utils/analytics';
import { AuthProvider } from '@/context/AuthContext';
import { CartProvider } from '@/context/CartContext';
import { LocationProvider } from '@/context/LocationContext';
import { OverlayProvider } from '@/context/OverlayContext';
import { WalletProvider } from '@/context/WalletContext';
import { ConsentProvider } from '@/components/consent/ConsentContext';
import ConsentBanner from '@/components/consent/ConsentBanner';
import ConsentSettingsModal from '@/components/consent/ConsentSettingsModal';
import Layout from '@/components/Layout';
import WeatherAlertBanner from '@/components/WeatherAlertBanner';
import { TutorialProvider, HelpButton } from '@/components/TutorialProvider';
import AuthCallback from '@/pages/AuthCallback';
import SharePage from '@/pages/SharePage';

// Lazy-loaded pages — split into separate module for bundle optimization
import {
  HomePage, AboutUs, BusinessDirectory, BusinessMap, BusinessDetail, AddBusiness,
  AIAssistant, Events, SubmitEvent, Resources, LouisianaBizResources, GlobalBizResources,
  MentalHealthTips, GratitudeWall, Grants, CommunityFeed, Shop, Cart, CheckoutSuccess,
  VirtualBooths, BoothDetail, BoothDashboard, CreateBooth, Donate, Contact, Blog, BlogPost,
  Services, WhyDowUrk, Partnerships, PressRelease, FundraiserPage,
  PhotographySuite, RealEstateSuite, RestaurantSuite, ConsultantSuite, IndustrySuites,
  Certifications as LegacyCertifications, TakedownPolicy, RefundPolicy, TermsOfUse, PrivacyPolicy, TermsOfService,
  CompliancePage, DataRequestPage, ContentRightsAgreement,
  ForgotPassword, Login, Register,
  AdminResourceManagement, AdminShopManagement, AdminBusinessManagement, AdminEventManagement,
  AdminBlessingModeration, AdminBlogManagement, AdminEventSubmissions, AdminDashboard, AdminHubDashboard,
  AdminCRM, AdminVolunteers, AdminVideoSettings, AdminNewsletter, AdminCampaigns,
  AdminSuccessStories, AdminServiceOrders, AdminDailyDigest, AdminContentGenerator,
  AdminAIAnalysis, AdminAIAgents, AdminLMS, AdminGeneralVolunteers, AdminOnboardingEmails,
  AdminEmailABTesting, AdminEmailTemplates, AdminTranslations, AdminAutomationRules,
  StripeTestDashboard, AdminRevenue, AdminEngagement, AdminReports, AdminBadgeAward,
  AdminBadgeTriggers, AdminGDPR, AdminServiceProviders, AdminEnterprise, AdminCapital,
  AdminROTWAnalytics, AdminPremiumSales, AdminUserManagement, AdminDonations, AdminFiles, AdminInterestForm, JoinInterest,
  AdminNewsletterSubscribers, AdminAnalyticsOverview, AdminEmailSequences,
  AdminCohortTracking, AdminDemographics, AdminSEEDWallets, AdminFeedback, AdminWhiteLabel,
  AdminRecipeApproval, AdminGlobalKitchen, SocialDesk, SocialVoice, SocialAnalytics,
  SEEDTokenDashboard, CommunityFeedback, CreativeProPricing, TenantPortal, TenantSettings,
  TenantLogin, TenantForgotPassword, TenantResetPassword, AcceptInvite,
  FreeBlueprint, BusinessBuilder, WorkforceProgram, DonorWall, Membership, MembershipSuccess,
  PremiumHub, GrantMatchAlerts, PremiumAnalytics, ComplianceCertification, PrioritySupport,
  DonationSuccess, TokenDashboard, CryptoDonation, ReferralProgram, DeFiLending, GrantConcierge,
  ComplianceConcierge, InstitutionalLicensing, FederalContracting, SOPBuilder, NonprofitOps, AdminTickerCMS,
  MentalWealthAudit, MentalWealthResources, AdminMentalWealthEmailDrip, AdminFeatureFlags,
  LearningDashboard, ModuleDetail, CohortEnrollment, EnrollmentSuccess, AICurriculum,
  AIFundamentals, MentorDirectory, Messages, AIGeneralistPricing, AIGeneralistRoadmap,
  AIToolsDirectory, PremiumAIDashboard, AIGeneralistLevel, AllTools, LaunchDay1,
  ProfessionalServices, ServiceOrderForm, CapitalReadinessHub, CapitalMarketplace,
  ServiceProviderMarketplace, EnterprisePortal, RetailLaunchpad, BusinessFormationWizard,
  PermitLookup, TestYourKnowledge,
  Forum, ForumCategory, ForumPost, ForumNewPost, BadgesPage, VolunteerHub,
  Proof, ProofDashboard, CreateCommitment, CommitmentDetail, ProofSquads, ProofApply,
  ProofEthics, ProofScoreboard, CohortDashboard, ProofPassport, ProofVerify, ProofLandingPage,
  ProofResiliencePathway, ResilienceWall, WinWall, GrantTracker, SkillsBarter, NextStep, DowUrkScore, PublicProfile, AdminGrants, PitchPractice, AdminNextStepLeaderboard, AdminResilienceFunnel, AdminCypTelemetry, AdminTopTasks, LaunchRoadmap, LaunchCalculator, CertificationTracker, NonprofitGrants, BusinessPlan, LenderMatch, BusinessStructure, DowUrkGrantsDashboard, Nonprofits, ProofForCohorts, ProofFreeTier, EmbedKit, NonprofitProfile, NonprofitDirectory, NonprofitSignup, NeedsAssessment, DowUrkImpact, StartANonprofit, NonprofitAdvocacy, Give, OrgProfile, Pricing, AdminCredentials, Verify, VerifyDossier, GrantScout, AdminGrantScout, NonprofitImpact, Simulators,
  YouthTrack, CommunityTrack, SevenFsMethodology, ResourceLibrary,
  Dashboard, MasterDashboard, BadgeProgressDashboard, AccountSettings,
  OrganizationManagement, APIAccessManagement, NotificationCenter, SubscriptionAnalytics,
  BusinessAnalytics, PremiumCalendar, PledgePage, OurPledgePage,
  DisasterHub, VeteransSupport, EmergencyResourcesMap, EmergencyAlerts, RecoveryNavigator,
  DisasterPreparedness, VolunteerPortal, GeneralVolunteerPortal, EmergencyContacts, WeatherMap,
  SevenFramework, SevenFPage,
  FinancialLiteracyHub, HealthScoreAssessment, StatementBuilder, CourseViewer,
  StudioLayout, StudioDashboard, StudioVault, StudioAI, StudioShoots, StudioClients,
  StudioBookings, StudioContracts, StudioInvoices, StudioScheduler, BookingRequest,
  WeatherAlertSettings, ClientPortalModule,
} from '@/routes/lazyPages';

// These barrel exports need special handling (named exports from lazy modules)
import { MyCertificates, VerifyCertificate } from '@/pages/Certificates';
import { FaithHub, FaithCurriculum, TribeFinder, DailyPractice, ReflectionJournal } from '@/pages/faith';
import { WorkforceHub, IntakeWizard, PathwayDashboard, EmployerDirectory, WraparoundHub } from '@/pages/workforce';
import SkillsAssessment from '@/pages/workforce/SkillsAssessment';
import { FitnessHub, MovementLibrary, HealthPassport, LegacyCoach } from '@/pages/fitness';
import { FoundationHub, VirtualSchool, CharacterStudio, ServiceLedger, WellnessSanctuary, MindDashboard } from '@/pages/foundation';
import { FoodHub, RecipeVault, CommunityHarvest, GlobalKitchenLive } from '@/pages/food';
import { LegacyStudioHub, ProductionBoard, CreativePathways, PortfolioGallery, MentorCircle, CreativeCoach, StudioCertifications } from '@/pages/legacystudio';
import ClientPortal, { ContractSign, InvoicePay, MagicLinkAuth } from '@/pages/portal/ClientPortal';
import Web3ComingSoon from '@/pages/Web3ComingSoon';

// Web3 feature flag — set REACT_APP_ENABLE_WEB3=false on prod hosts that don't
// support blockchain runtime. Routes still resolve (good for SEO + shared
// links) but show a Coming Soon page instead of the live wallet UI.
const WEB3_ENABLED = (process.env.REACT_APP_ENABLE_WEB3 ?? 'true').toString().toLowerCase() !== 'false';
const CryptoDonationGated = WEB3_ENABLED ? CryptoDonation : () => <Web3ComingSoon feature="Crypto donations" />;
const TokenDashboardGated = WEB3_ENABLED ? TokenDashboard : () => <Web3ComingSoon feature="The $SEED Token Dashboard" />;
const SEEDTokenDashboardGated = WEB3_ENABLED ? SEEDTokenDashboard : () => <Web3ComingSoon feature="The $SEED Token Dashboard" />;
const DeFiLendingGated = WEB3_ENABLED ? DeFiLending : () => <Web3ComingSoon feature="The DowUrk DeFi Lending Portal" />;

// Loading fallback
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="text-center">
        <div className="w-10 h-10 border-3 border-[#006847] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-gray-500 text-sm">Loading...</p>
      </div>
    </div>
  );
}

// Page view tracker component
function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
    // recordVisit returns true exactly once when the visit count crosses the suggest-pin
    // threshold for an unpinned, not-yet-dismissed page — fire a nudge toast in that case.
    const shouldSuggest = recordVisit(location.pathname);
    if (!shouldSuggest) return;
    const label = ROUTE_LABELS[location.pathname]?.label || 'this page';
    toast(`You've been here a lot — pin ${label} for one-tap access?`, {
      duration: 12000,
      action: {
        label: 'Pin it',
        onClick: () => {
          const result = togglePin(location.pathname);
          if (result === true) toast.success(`Pinned ${label}`);
          else if (result === null) toast.error('Pin limit reached (8). Unpin something first.');
        },
      },
      cancel: {
        label: 'Dismiss',
        onClick: () => dismissPinSuggestion(location.pathname),
      },
    });
  }, [location.pathname, location.search]);
  return null;
}

// Admin workspaces do not need geolocation prompts or public weather polling.
function PublicWeatherBanner() {
  const location = useLocation();
  return location.pathname.startsWith('/admin') ? null : <WeatherAlertBanner showSettings={true} />;
}

// Initialize Google Analytics on app load
if (typeof window !== 'undefined') {
  initGA();
}

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <LocationProvider>
          <OverlayProvider>
          <WalletProvider>
          <ConsentProvider>
          <TutorialProvider>
          <ImageProtectionProvider>
          <BrowserRouter>
            {/* Global Toast container — mounted BEFORE PageViewTracker so the sonner
                Observer has a subscriber registered by the time the tracker effect
                fires toast() on the threshold visit. Placed OUTSIDE Suspense so it
                stays mounted through lazy-page transitions. */}
            <Toaster position="bottom-center" richColors closeButton />
            <PageViewTracker />
            <PublicWeatherBanner />
            <HelpButton />
            <Suspense fallback={<PageLoader />}>
            <Routes>
          <Route path="/" element={<Layout />}>
            <Route index element={<HomePage />} />
            <Route path="about" element={<AboutUs />} />
            <Route path="our-pledge" element={<OurPledgePage />} />
            <Route path="fundraiser" element={<FundraiserPage />} />
            <Route path="dream-project" element={<FundraiserPage />} />
            <Route path="press-release" element={<PressRelease />} />
            <Route path="news" element={<PressRelease />} />
            <Route path="businesses" element={<BusinessDirectory />} />
            <Route path="businesses/:id" element={<BusinessDetail />} />
            <Route path="business-map" element={<BusinessMap />} />
            <Route path="add-business" element={<AddBusiness />} />
            <Route path="services" element={<Services />} />
            <Route path="professional-services" element={<ProfessionalServices />} />
            <Route path="service-order" element={<ServiceOrderForm />} />
            <Route path="ai-assistant" element={<AIAssistant />} />
            <Route path="ai-curriculum" element={<AICurriculum />} />
            <Route path="ai-fundamentals" element={<AIFundamentals />} />
            <Route path="events" element={<Events />} />
            <Route path="submit-event" element={<SubmitEvent />} />
            <Route path="resources" element={<Resources />} />
            <Route path="louisiana-biz-resources" element={<LouisianaBizResources />} />
            <Route path="global-biz-resources" element={<GlobalBizResources />} />
            <Route path="mental-health" element={<MentalHealthTips />} />
            <Route path="test-your-knowledge" element={<TestYourKnowledge />} />
            <Route path="business-formation" element={<BusinessFormationWizard />} />
            <Route path="permit-lookup" element={<PermitLookup />} />
            <Route path="gratitude-wall" element={<GratitudeWall />} />
            <Route path="partnerships" element={<Partnerships />} />
            <Route path="grants" element={<Grants />} />
            <Route path="community" element={<CommunityFeed />} />
            <Route path="forum" element={<Forum />} />
            <Route path="forum/category/:categoryId" element={<ForumCategory />} />
            <Route path="forum/post/:postId" element={<ForumPost />} />
            <Route path="forum/new" element={<ForumNewPost />} />
            <Route path="shop" element={<Shop />} />
            <Route path="cart" element={<Cart />} />
            <Route path="checkout/success" element={<CheckoutSuccess />} />
            <Route path="membership" element={<Membership />} />
            <Route path="membership/success" element={<MembershipSuccess />} />
            <Route path="why-dowurk" element={<WhyDowUrk />} />
            <Route path="photography-suite" element={<PhotographySuite />} />
            <Route path="real-estate-suite" element={<RealEstateSuite />} />
            <Route path="restaurant-suite" element={<RestaurantSuite />} />
            <Route path="consultant-suite" element={<ConsultantSuite />} />
            <Route path="industry-suites" element={<IndustrySuites />} />
            <Route path="ai-generalist/level/:levelId" element={<AIGeneralistLevel />} />
            <Route path="badges" element={<BadgesPage />} />
            <Route path="volunteer-hub" element={<VolunteerHub />} />
            <Route path="capital-readiness" element={<CapitalReadinessHub />} />
            <Route path="retail-launchpad" element={<RetailLaunchpad />} />
            <Route path="referrals" element={<ReferralProgram />} />
            <Route path="proof" element={<ProofLandingPage />} />
            <Route path="proof/accountability" element={<Proof />} />
            <Route path="youth-track" element={<YouthTrack />} />
            <Route path="community-track" element={<CommunityTrack />} />
            <Route path="seven-fs" element={<SevenFsMethodology />} />
            <Route path="resource-library" element={<ResourceLibrary />} />
            <Route path="proof/dashboard" element={<ProofDashboard />} />
            <Route path="proof/create" element={<CreateCommitment />} />
            <Route path="proof/commitment/:id" element={<CommitmentDetail />} />
            <Route path="proof/squads" element={<ProofSquads />} />
            <Route path="proof/squad/:id" element={<ProofSquads />} />
            <Route path="proof/apply" element={<ProofApply />} />
            <Route path="proof/ethics" element={<ProofEthics />} />
            <Route path="proof/pulse" element={<ProofScoreboard />} />
            <Route path="proof/cohort" element={<CohortDashboard />} />
            <Route path="proof/resilience-pathway" element={<ProofResiliencePathway />} />
            <Route path="proof/resilience-wall" element={<ResilienceWall />} />
            <Route path="wins" element={<WinWall />} />
            <Route path="grants/tracker" element={<GrantTracker />} />
            <Route path="skills-barter" element={<SkillsBarter />} />
            <Route path="next-step" element={<NextStep />} />
            <Route path="dowurk-score" element={<DowUrkScore />} />
            <Route path="u/:userId" element={<PublicProfile />} />
            <Route path="launch-roadmap" element={<LaunchRoadmap />} />
            <Route path="launch-calculator" element={<LaunchCalculator />} />
            <Route path="certifications" element={<CertificationTracker />} />
            <Route path="nonprofit-grants" element={<NonprofitGrants />} />
            <Route path="business-plan" element={<BusinessPlan />} />
            <Route path="lender-match" element={<LenderMatch />} />
            <Route path="business-structure" element={<BusinessStructure />} />
            <Route path="dowurk/grants" element={<DowUrkGrantsDashboard />} />
            <Route path="nonprofits" element={<Nonprofits />} />
            <Route path="nonprofits/proof-for-cohorts" element={<ProofForCohorts />} />
            <Route path="nonprofits/proof-free-tier" element={<ProofFreeTier />} />
            <Route path="nonprofits/embed-kit" element={<EmbedKit />} />
            <Route path="nonprofits/directory" element={<NonprofitDirectory />} />
            <Route path="nonprofits/signup" element={<NonprofitSignup />} />
            <Route path="nonprofits/start-a-nonprofit" element={<StartANonprofit />} />
            <Route path="nonprofits/advocacy" element={<NonprofitAdvocacy />} />
            <Route path="give" element={<Give />} />
            <Route path="profile" element={<OrgProfile />} />
            <Route path="pricing" element={<Pricing />} />
            <Route path="nonprofits/profile/:slug" element={<NonprofitProfile />} />
            <Route path="needs-assessment" element={<NeedsAssessment />} />
            <Route path="dowurk/impact" element={<DowUrkImpact />} />
            <Route path="verify/:assertionId" element={<Verify />} />
            <Route path="verify" element={<VerifyDossier />} />
            <Route path="grant-scout" element={<GrantScout />} />
            <Route path="admin/grant-scout" element={<AdminGrantScout />} />
            <Route path="nonprofits/impact/:slug" element={<NonprofitImpact />} />
            <Route path="simulators" element={<Simulators />} />
            <Route path="admin/grants" element={<AdminGrants />} />
            <Route path="admin/credentials" element={<AdminCredentials />} />
            <Route path="pitch-practice" element={<PitchPractice />} />
            <Route path="admin/next-step-leaderboard" element={<AdminNextStepLeaderboard />} />
            <Route path="admin/resilience-funnel" element={<AdminResilienceFunnel />} />
            <Route path="admin/cyp-telemetry" element={<AdminCypTelemetry />} />
            <Route path="admin/top-tasks" element={<AdminTopTasks />} />
            <Route path="proof/:username" element={<ProofPassport />} />
            <Route path="verify/:badgeId/:recipientId" element={<ProofVerify />} />
            <Route path="calendar" element={<PremiumCalendar />} />
            <Route path="account/settings" element={<AccountSettings />} />
            <Route path="organization" element={<OrganizationManagement />} />
            <Route path="developer/api-keys" element={<APIAccessManagement />} />
            <Route path="notifications" element={<NotificationCenter />} />
            <Route path="business/analytics" element={<BusinessAnalytics />} />
            <Route path="virtual-booths" element={<VirtualBooths />} />
            <Route path="booth/create" element={<CreateBooth />} />
            <Route path="booth/dashboard" element={<BoothDashboard />} />
            <Route path="booth/:boothId" element={<BoothDetail />} />
            <Route path="premium" element={<PremiumHub />} />
            <Route path="premium/grants" element={<GrantMatchAlerts />} />
            <Route path="premium/analytics" element={<PremiumAnalytics />} />
            <Route path="premium/support" element={<PrioritySupport />} />
            <Route path="donate" element={<Donate />} />
            <Route path="donate/success" element={<DonationSuccess />} />
            <Route path="donate/crypto" element={<CryptoDonationGated />} />
            <Route path="crypto-donation" element={<CryptoDonationGated />} />
            <Route path="crypto-payment-success" element={<DonationSuccess />} />
            <Route path="token" element={<TokenDashboardGated />} />
            <Route path="contact" element={<Contact />} />
            <Route path="blog" element={<Blog />} />
            <Route path="blog/:slug" element={<BlogPost />} />
            <Route path="privacy-policy" element={<PrivacyPolicy />} />
            <Route path="terms-of-use" element={<TermsOfService />} />
            <Route path="terms-of-service" element={<TermsOfService />} />
            <Route path="compliance" element={<CompliancePage />} />
            <Route path="data-request" element={<DataRequestPage />} />
            <Route path="capital-marketplace" element={<CapitalMarketplace />} />
            <Route path="defi-lending" element={<DeFiLendingGated />} />
            <Route path="grant-concierge" element={<GrantConcierge />} />
            <Route path="compliance-concierge" element={<ComplianceConcierge />} />
            <Route path="institutional-licensing" element={<InstitutionalLicensing />} />
            <Route path="federal-contracting" element={<FederalContracting />} />
            <Route path="sop-builder" element={<SOPBuilder />} />
            <Route path="nonprofit-ops" element={<NonprofitOps />} />
            <Route path="admin/ticker" element={<AdminTickerCMS />} />
            <Route path="mental-wealth" element={<Navigate to="/mental-health" replace />} />
            <Route path="mental-health/audit" element={<MentalWealthAudit />} />
            <Route path="mental-health/resources" element={<MentalWealthResources />} />
            {/* Legacy aliases — kept so print flyers / QR codes / old email links keep working */}
            <Route path="mental-wealth/audit" element={<Navigate to="/mental-health/audit" replace />} />
            <Route path="mental-wealth/resources" element={<Navigate to="/mental-health/resources" replace />} />
            <Route path="admin/mental-wealth/email-drip" element={<AdminMentalWealthEmailDrip />} />
            <Route path="admin/feature-flags" element={<AdminFeatureFlags />} />
            <Route path="service-providers" element={<ServiceProviderMarketplace />} />
            <Route path="enterprise" element={<EnterprisePortal />} />
            <Route path="terms-of-use" element={<TermsOfUse />} />
            <Route path="refund-policy" element={<RefundPolicy />} />
            <Route path="takedown-policy" element={<TakedownPolicy />} />
            <Route path="content-rights" element={<ContentRightsAgreement />} />
            <Route path="compliance" element={<ComplianceCertification />} />
            <Route path="pledge" element={<PledgePage />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="master-dashboard" element={<MasterDashboard />} />
            <Route path="badge-progress" element={<BadgeProgressDashboard />} />
            <Route path="admin" element={<AdminHubDashboard />} />
            <Route path="admin/legacy" element={<AdminDashboard />} />
            <Route path="admin/businesses" element={<AdminBusinessManagement />} />
            <Route path="admin/events" element={<AdminEventManagement />} />
            <Route path="admin/blessings" element={<AdminBlessingModeration />} />
            <Route path="admin/resources" element={<AdminResourceManagement />} />
            <Route path="admin/blog" element={<AdminBlogManagement />} />
            <Route path="admin/users" element={<AdminUserManagement />} />
            <Route path="admin/event-submissions" element={<AdminEventSubmissions />} />
            <Route path="admin/shop" element={<AdminShopManagement />} />
            <Route path="admin/crm" element={<AdminCRM />} />
            <Route path="admin/volunteers" element={<AdminVolunteers />} />
            <Route path="admin/analytics" element={<SubscriptionAnalytics />} />
            <Route path="admin/video" element={<AdminVideoSettings />} />
            <Route path="admin/newsletter" element={<AdminNewsletter />} />
            <Route path="admin/campaigns" element={<AdminCampaigns />} />
            <Route path="admin/success-stories" element={<AdminSuccessStories />} />
            <Route path="admin/service-orders" element={<AdminServiceOrders />} />
            <Route path="admin/badge-award" element={<AdminBadgeAward />} />
            <Route path="admin/badge-triggers" element={<AdminBadgeTriggers />} />
            <Route path="admin/gdpr" element={<AdminGDPR />} />
            <Route path="admin/service-providers" element={<AdminServiceProviders />} />
            <Route path="admin/enterprise" element={<AdminEnterprise />} />
            <Route path="admin/capital" element={<AdminCapital />} />
            <Route path="admin/rotw-analytics" element={<AdminROTWAnalytics />} />
            <Route path="admin/premium-sales" element={<AdminPremiumSales />} />
            <Route path="admin/user-management" element={<AdminUserManagement />} />
            <Route path="admin/files" element={<AdminFiles />} />
            <Route path="admin/interest-form" element={<AdminInterestForm />} />
            <Route path="admin/social" element={<SocialDesk />} />
            <Route path="admin/social/voice" element={<SocialVoice />} />
            <Route path="admin/social/analytics" element={<SocialAnalytics />} />
            <Route path="admin/donations" element={<AdminDonations />} />
            <Route path="admin/newsletter-subscribers" element={<AdminNewsletterSubscribers />} />
            <Route path="admin/analytics-overview" element={<AdminAnalyticsOverview />} />
            <Route path="admin/email-sequences" element={<AdminEmailSequences />} />
            <Route path="admin/cohort-tracking" element={<AdminCohortTracking />} />
            <Route path="admin/demographics" element={<AdminDemographics />} />
            <Route path="admin/seed-wallets" element={<AdminSEEDWallets />} />
            <Route path="admin/feedback" element={<AdminFeedback />} />
            <Route path="admin/whitelabel" element={<AdminWhiteLabel />} />
            <Route path="seed-token" element={<SEEDTokenDashboardGated />} />
            <Route path="community-feedback" element={<CommunityFeedback />} />
            <Route path="creative-pro-pricing" element={<CreativeProPricing />} />
            <Route path="tenant/:slug" element={<TenantPortal />} />
            <Route path="tenant/:slug/login" element={<TenantLogin />} />
            <Route path="tenant/:slug/forgot-password" element={<TenantForgotPassword />} />
            <Route path="tenant/:slug/settings" element={<TenantSettings />} />
            <Route path="tenant/invite/:token" element={<AcceptInvite />} />
            <Route path="tenant/reset-password/:token" element={<TenantResetPassword />} />
            
            {/* Funnel Pages */}
            <Route path="free-blueprint" element={<FreeBlueprint />} />
            <Route path="business-builder" element={<BusinessBuilder />} />
            <Route path="workforce-program" element={<WorkforceProgram />} />
            <Route path="admin/recipe-approval" element={<AdminRecipeApproval />} />
            <Route path="admin/global-kitchen" element={<AdminGlobalKitchen />} />
            <Route path="admin/daily-digest" element={<AdminDailyDigest />} />
            <Route path="admin/content-generator" element={<AdminContentGenerator />} />
            <Route path="admin/ai-analysis" element={<AdminAIAnalysis />} />
            <Route path="admin/ai-agents" element={<AdminAIAgents />} />
            <Route path="admin/lms" element={<AdminLMS />} />
            <Route path="admin/general-volunteers" element={<AdminGeneralVolunteers />} />
            <Route path="admin/onboarding-emails" element={<AdminOnboardingEmails />} />
            <Route path="admin/email-ab-testing" element={<AdminEmailABTesting />} />
            <Route path="admin/email-templates" element={<AdminEmailTemplates />} />
            <Route path="admin/automation-rules" element={<AdminAutomationRules />} />
            <Route path="admin/stripe-testing" element={<StripeTestDashboard />} />
            <Route path="admin/revenue" element={<AdminRevenue />} />
            <Route path="admin/engagement" element={<AdminEngagement />} />
            <Route path="admin/reports" element={<AdminReports />} />
            <Route path="admin/translations" element={<AdminTranslations />} />
            <Route path="donor-wall" element={<DonorWall />} />
            <Route path="lms" element={<LearningDashboard />} />
            <Route path="lms/enroll" element={<CohortEnrollment />} />
            <Route path="lms/enroll/success" element={<EnrollmentSuccess />} />
            <Route path="lms/module/:moduleId" element={<ModuleDetail />} />
            <Route path="mentors" element={<MentorDirectory />} />
            <Route path="messages" element={<Messages />} />
            <Route path="ai-generalist" element={<AIGeneralistPricing />} />
            <Route path="ai-generalist/pricing" element={<AIGeneralistPricing />} />
            <Route path="ai-generalist/roadmap" element={<AIGeneralistRoadmap />} />
            <Route path="ai-generalist/tools" element={<AIToolsDirectory />} />
            <Route path="tools" element={<AllTools />} />
            <Route path="launch" element={<LaunchDay1 />} />
            <Route path="premium/ai-dashboard" element={<PremiumAIDashboard />} />
            <Route path="veterans" element={<VeteransSupport />} />
            <Route path="disaster-hub" element={<DisasterHub />} />
            <Route path="disaster-hub/resources" element={<EmergencyResourcesMap />} />
            <Route path="disaster-hub/alerts" element={<EmergencyAlerts />} />
            <Route path="disaster-hub/recovery" element={<RecoveryNavigator />} />
            <Route path="disaster-hub/preparedness" element={<DisasterPreparedness />} />
            <Route path="disaster-hub/volunteer" element={<VolunteerPortal />} />
            <Route path="volunteer" element={<GeneralVolunteerPortal />} />
            <Route path="disaster-hub/contacts" element={<EmergencyContacts />} />
            <Route path="disaster-hub/weather" element={<WeatherMap />} />
            <Route path="framework" element={<SevenFramework />} />
            <Route path="framework/faith" element={<SevenFPage fName="Faith" />} />
            <Route path="framework/fitness" element={<SevenFPage fName="Fitness" />} />
            <Route path="framework/foundation" element={<SevenFPage fName="Foundation" />} />
            <Route path="framework/fashion" element={<SevenFPage fName="Fashion" />} />
            <Route path="framework/film" element={<SevenFPage fName="Film" />} />
            <Route path="framework/food" element={<SevenFPage fName="Food" />} />
            <Route path="framework/finances" element={<SevenFPage fName="Finances" />} />
            
            {/* Financial Literacy Suite */}
            <Route path="financial-literacy" element={<FinancialLiteracyHub />} />
            <Route path="financial-literacy/health-score" element={<HealthScoreAssessment />} />
            <Route path="financial-literacy/statement-builder" element={<StatementBuilder />} />
            <Route path="financial-literacy/courses" element={<CourseViewer />} />
            <Route path="financial-literacy/courses/:trackId/:moduleId/:lessonId" element={<CourseViewer />} />
            <Route path="financial-literacy/certificates" element={<MyCertificates />} />
            <Route path="financial-literacy/verify/:certificateNumber" element={<VerifyCertificate />} />

            {/* Faith Platform (F1) */}
            <Route path="faith" element={<FaithHub />} />
            <Route path="faith/curriculum" element={<FaithCurriculum />} />
            <Route path="faith/curriculum/:moduleId" element={<FaithCurriculum />} />
            <Route path="faith/tribes" element={<TribeFinder />} />
            <Route path="faith/practice" element={<DailyPractice />} />
            <Route path="faith/journal" element={<ReflectionJournal />} />

            {/* Workforce Development (F5) */}
            <Route path="workforce" element={<WorkforceHub />} />
            <Route path="workforce/assessment" element={<IntakeWizard />} />
            <Route path="workforce/skills-assessment" element={<SkillsAssessment />} />
            <Route path="workforce/pathway" element={<PathwayDashboard />} />
            <Route path="workforce/employers" element={<EmployerDirectory />} />
            <Route path="workforce/support" element={<WraparoundHub />} />

            {/* Fitness Platform (LegacyFit - F4) */}
            <Route path="fitness" element={<FitnessHub />} />
            <Route path="fitness/library" element={<MovementLibrary />} />
            <Route path="fitness/library/:workoutId" element={<MovementLibrary />} />
            <Route path="fitness/passport" element={<HealthPassport />} />
            <Route path="fitness/coach" element={<LegacyCoach />} />

            {/* Foundation Platform (Legacy Mind Center - F3) */}
            <Route path="foundation" element={<FoundationHub />} />
            <Route path="foundation/school" element={<VirtualSchool />} />
            <Route path="foundation/school/:courseId" element={<VirtualSchool />} />
            <Route path="foundation/character" element={<CharacterStudio />} />
            <Route path="foundation/service" element={<ServiceLedger />} />
            <Route path="foundation/wellness" element={<WellnessSanctuary />} />
            <Route path="foundation/dashboard" element={<MindDashboard />} />

            {/* Food Platform (Farm2Table - F4) */}
            <Route path="food" element={<FoodHub />} />
            <Route path="food/vault" element={<RecipeVault />} />
            <Route path="food/vault/:recipeId" element={<RecipeVault />} />
            <Route path="food/harvest" element={<CommunityHarvest />} />
            <Route path="food/kitchen" element={<FoodHub />} />
            <Route path="food/kitchen/live" element={<GlobalKitchenLive />} />
            <Route path="food/guilds" element={<FoodHub />} />

            {/* Fashion/Film Platform (LegacyStudio - F6) */}
            <Route path="legacystudio" element={<LegacyStudioHub />} />
            <Route path="legacystudio/productions" element={<ProductionBoard />} />
            <Route path="legacystudio/productions/:productionId" element={<ProductionBoard />} />
            <Route path="legacystudio/pathways" element={<CreativePathways />} />
            <Route path="legacystudio/pathways/:pathwayId" element={<CreativePathways />} />
            <Route path="legacystudio/portfolio" element={<PortfolioGallery />} />
            <Route path="legacystudio/mentors" element={<MentorCircle />} />
            <Route path="legacystudio/coach" element={<CreativeCoach />} />
            <Route path="legacystudio/certifications" element={<StudioCertifications />} />
          </Route>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/f/:slug" element={<SharePage />} />
          <Route path="/join" element={<JoinInterest />} />
          
          {/* RJBrown Photography Studio */}
          <Route path="/studio" element={<StudioLayout />}>
            <Route index element={<StudioDashboard />} />
            <Route path="vault" element={<StudioVault />} />
            <Route path="ai" element={<StudioAI />} />
            <Route path="shoots" element={<StudioShoots />} />
            <Route path="clients" element={<StudioClients />} />
            <Route path="bookings" element={<StudioBookings />} />
            <Route path="contracts" element={<StudioContracts />} />
            <Route path="invoices" element={<StudioInvoices />} />
            <Route path="scheduler" element={<StudioScheduler />} />
          </Route>
          
          <Route path="/book" element={<BookingRequest />} />
          <Route path="/alerts" element={<WeatherAlertSettings />} />
          <Route path="/portal" element={<ClientPortal />} />
          <Route path="/portal/contract/:shareToken" element={<ContractSign />} />
          <Route path="/portal/invoice/:shareToken" element={<InvoicePay />} />
          <Route path="/portal/auth/:magicToken" element={<MagicLinkAuth />} />
        </Routes>
            </Suspense>
            <ConsentBanner />
            <ConsentSettingsModal />
      </BrowserRouter>
          </ImageProtectionProvider>
          </TutorialProvider>
          </ConsentProvider>
          </WalletProvider>
          </OverlayProvider>
        </LocationProvider>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
